import React, { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../../services/api';
import { Device as TwilioDevice } from '@twilio/voice-sdk';

type CallPanelProps = {
  callId: string;
  conferenceName?: string;
  initialStatus?: string;
  displayName?: string;
  onClose?: () => void;
};

type CallStatusPayload = {
  callId: string;
  status: string;
  duration?: number;
  recordingUrl?: string;
};

const POLL_INTERVAL_MS = 2000;

export const CallPanel: React.FC<CallPanelProps> = ({ callId, conferenceName, initialStatus, displayName, onClose }) => {
  const [status, setStatus] = useState<string>(initialStatus || 'queued');
  const [recordingUrl, setRecordingUrl] = useState<string | undefined>();
  const [duration, setDuration] = useState<number | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [startedAt] = useState<number>(() => Date.now());
  const [conferenceSid, setConferenceSid] = useState<string | undefined>(undefined);
  const timerRef = useRef<number | null>(null);
  const conferenceTimerRef = useRef<number | null>(null);
  const [muted, setMuted] = useState<boolean>(false);
  const [speakerOn, setSpeakerOn] = useState<boolean>(true);
  const deviceRef = useRef<any>(null);
  const connectionRef = useRef<any>(null);

  const humanStatus = useMemo(() => {
    switch (status) {
      case 'queued':
      case 'initiated':
        return 'Queued';
      case 'ringing':
        return 'Ringing';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'busy':
        return 'Busy';
      case 'failed':
      case 'no-answer':
      case 'canceled':
        return 'Failed';
      default:
        return status;
    }
  }, [status]);

  useEffect(() => {
    let isMounted = true;

    const poll = async () => {
      try {
        const resp = await apiService.getCallStatus(callId);
        if (!resp.success || !resp.status) return;
        const payload = resp.status as CallStatusPayload;
        if (!isMounted) return;
        setStatus(payload.status);
        setDuration(payload.duration);
        setRecordingUrl(payload.recordingUrl);
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message || 'Failed to fetch call status');
      }
    };

    // initial immediately, then interval
    poll();
    timerRef.current = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [callId]);

  // Poll for conference SID when conferenceName is available but conferenceSid is undefined
  useEffect(() => {
    if (!conferenceName || conferenceSid) return;

    let isMounted = true;

    const pollConference = async () => {
      try {
        const result = await apiService.getConferenceByFriendlyName(conferenceName);
        if (!isMounted) return;
        
        if (result.success && result.conference) {
          setConferenceSid(result.conference.conferenceSid);
          console.log('Conference SID found:', result.conference.conferenceSid);
        }
      } catch (e: any) {
        if (!isMounted) return;
        console.warn('Conference polling error:', e?.message);
      }
    };

    // Poll immediately, then every 3 seconds
    pollConference();
    conferenceTimerRef.current = window.setInterval(pollConference, 3000);

    return () => {
      isMounted = false;
      if (conferenceTimerRef.current) window.clearInterval(conferenceTimerRef.current);
    };
  }, [conferenceName, conferenceSid]);

  const [isJoining, setIsJoining] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  const joinFromBrowser = async () => {
    if (isJoining || isConnected) return;
    
    try {
      setIsJoining(true);
      setError(null);
      
      console.log('=== Starting joinFromBrowser ===');
      console.log('Joining conference from browser...', conferenceName);
      
      // Fetch token from backend first
      const { token } = await apiService.getVoiceAccessToken(conferenceName || 'default');
      console.log('Got voice token, creating device...');
      console.log('Twilio Device export:', TwilioDevice);
      console.log('TwilioDevice.create exists:', typeof (TwilioDevice as any)?.create);
      
      // Create device (supports both v2 (Device.create) and legacy v1 (new Device()))
      let device: any;
      const deviceOptions: any = { codecPreferences: ['opus', 'pcmu'], logLevel: 'debug' };
      if (typeof (TwilioDevice as any)?.create === 'function') {
        console.log('Using Device.create (v2)');
        device = await (TwilioDevice as any).create(token, deviceOptions);
      } else {
        console.log('Device.create not found. Falling back to new Device(token, options) (v1)');
        device = new (TwilioDevice as unknown as any)(token, deviceOptions);
      }
      deviceRef.current = device;
      
      // Set up device event listeners
      device.on('registered', () => {
        console.log('Device registered successfully');
      });
      
      device.on('error', (error: any) => {
        console.error('Device error:', error);
        setError(`Device error: ${error.message}`);
        setIsJoining(false);
      });
      
      console.log('Connecting to conference...', conferenceName);
      const conn = await device.connect({ 
        params: { 
          conferenceName: conferenceName || 'default' 
        } 
      });
      connectionRef.current = conn;
      
      // Set up connection event listeners
      conn.on('accept', () => {
        console.log('Connection accepted - you can now talk!');
        setIsConnected(true);
        setIsJoining(false);
        setError(null);
      });
      
      conn.on('disconnect', () => {
        console.log('Connection disconnected');
        setIsConnected(false);
      });
      
      conn.on('error', (error: any) => {
        console.error('Connection error:', error);
        setError(`Connection error: ${error.message}`);
        setIsConnected(false);
        setIsJoining(false);
      });
      
    } catch (e: any) {
      console.error('=== ERROR in joinFromBrowser ===');
      console.error('Error details:', e);
      console.error('Error message:', e?.message);
      console.error('Error stack:', e?.stack);
      setError(`Failed to join from browser: ${e?.message || 'Unknown error'}`);
      setIsJoining(false);
    }
  };

  const hangUp = async () => {
    try {
      // Disconnect from conference first
      if (connectionRef.current) {
        connectionRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.disconnectAll();
      }
      
      await apiService.endCall(callId);
      setStatus('completed');
    } catch (e) {
      // ignore
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connectionRef.current) {
        connectionRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.disconnectAll();
      }
    };
  }, []);

  const formattedDuration = useMemo(() => {
    if (typeof duration !== 'number' || Number.isNaN(duration)) {
      const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      const m = Math.floor(seconds / 60).toString().padStart(2, '0');
      const s = (seconds % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    }
    const m = Math.floor(duration / 60).toString().padStart(2, '0');
    const s = (duration % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, [duration, startedAt]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden bg-[#0b1220] text-white shadow-2xl border border-white/10">
        <div className="flex flex-col items-center py-10 px-6">
          <div className="h-28 w-28 rounded-full bg-white/10 flex items-center justify-center mb-5">
            <i className="fa-solid fa-user text-4xl text-white/80"></i>
          </div>
          <div className="text-xl font-semibold">
            {displayName || 'Calling'}
          </div>
          <div className="text-sm text-white/70 mt-1">{formattedDuration}</div>
          <div className="text-xs text-white/60 mt-2">{humanStatus}</div>
          {isConnected && (
            <div className="text-xs text-green-400 mt-1 flex items-center gap-1">
              <i className="fa-solid fa-check-circle"></i>
              Connected to conference - You can talk now!
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            {!isConnected ? (
              <button
                onClick={joinFromBrowser}
                disabled={isJoining}
                className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  isJoining 
                    ? 'bg-yellow-600 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
                title={isJoining ? 'Joining...' : 'Join via browser to talk'}
              >
                <i className={`fa-solid ${isJoining ? 'fa-spinner fa-spin' : 'fa-microphone'}`}></i>
              </button>
            ) : (
              <button
                onClick={() => {
                  try {
                    connectionRef.current?.disconnect();
                    deviceRef.current?.disconnectAll();
                    setIsConnected(false);
                  } catch (e) {
                    console.error('Error disconnecting:', e);
                  }
                }}
                className="h-12 w-12 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
                title="Disconnect from conference"
              >
                <i className="fa-solid fa-phone-slash"></i>
              </button>
            )}
            <button
              onClick={() => setMuted(!muted)}
              className={`h-12 w-12 rounded-full flex items-center justify-center ${muted ? 'bg-white/20 text-white' : 'bg-white/10 text-white'}`}
              title={muted ? 'Unmute (placeholder)' : 'Mute (placeholder)'}
            >
              <i className={`fa-solid ${muted ? 'fa-microphone-slash' : 'fa-microphone'}`}></i>
            </button>
            <button
              onClick={() => {
                hangUp();
                try { connectionRef.current?.disconnect(); } catch {}
                try { deviceRef.current?.disconnectAll(); } catch {}
              }}
              className="h-12 w-12 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
              title="Hang up"
            >
              <i className="fa-solid fa-phone"></i>
            </button>
            <button
              onClick={() => setSpeakerOn(!speakerOn)}
              className={`h-12 w-12 rounded-full flex items-center justify-center ${speakerOn ? 'bg-white/10 text-white' : 'bg-white/20 text-white'}`}
              title={speakerOn ? 'Speaker on (placeholder)' : 'Speaker off (placeholder)'}
            >
              <i className="fa-solid fa-volume-high"></i>
            </button>
            <button
              onClick={onClose}
              className="h-12 w-12 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20"
              title="Close"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div className="mt-6 text-xs text-white/60 w-full text-center">
            {conferenceName ? <span title={conferenceName}>Conf: {conferenceName}</span> : null}
            {conferenceSid ? <span className="ml-3" title={conferenceSid}>SID: {conferenceSid}</span> : null}
            {recordingUrl ? (
              <span className="ml-3">
                Recording: <a className="underline" href={recordingUrl.startsWith('http') ? recordingUrl : `https://api.twilio.com${recordingUrl}`} target="_blank" rel="noreferrer">Open</a>
              </span>
            ) : null}
            {error ? <div className="text-red-400 mt-2">{error}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallPanel;


